"use client";

import { useEffect, useState } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { Search as SearchIcon, SlidersHorizontal, X } from "lucide-react";

interface Props {
  /** Total active filter count, shown as a badge on the "Filtros" button. */
  filterCount?: number;
  /** Callback to open the mobile drawer. When omitted, the button is hidden
   *  (e.g. legacy layout where filters live in a static sidebar). */
  onOpenFilters?: () => void;
  /** Optional sort selector rendered inline (mobile-friendly). */
  sortControl?: React.ReactNode;
  placeholder?: string;
}

/**
 * Sticky search bar that NEVER disappears. Sits below the navbar at all
 * breakpoints and houses: query input + active count + open-filters button
 * (mobile/tablet) + optional sort selector.
 *
 * The search input writes back to `?q=` via router.replace on submit; while
 * typing, local state holds the value so we don't thrash the URL on every key.
 */
export function StickySearchBar({
  filterCount = 0,
  onOpenFilters,
  sortControl,
  placeholder = "Buscar productos, marcas, tiendas…",
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [value, setValue] = useState(searchParams.get("q") ?? "");

  useEffect(() => {
    setValue(searchParams.get("q") ?? "");
  }, [searchParams]);

  const submit = (next: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (next.trim()) params.set("q", next.trim());
    else params.delete("q");
    params.delete("page");
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  return (
    <div className="sticky top-14 sm:top-16 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2.5 bg-background/85 backdrop-blur-md border-b border-border/60">
      <div className="flex items-center gap-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(value);
          }}
          className="relative flex-1 min-w-0"
          role="search"
        >
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="search"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            aria-label="Buscar"
            className="w-full h-10 pl-9 pr-9 rounded-full border border-input bg-card text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/40"
          />
          {value && (
            <button
              type="button"
              onClick={() => {
                setValue("");
                submit("");
              }}
              aria-label="Limpiar búsqueda"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full hover:bg-muted flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          )}
        </form>

        {onOpenFilters && (
          <button
            type="button"
            onClick={onOpenFilters}
            className="lg:hidden relative inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full border bg-card hover:bg-muted text-sm font-semibold transition-colors"
            aria-label={`Filtros${filterCount ? `: ${filterCount} activos` : ""}`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filtros
            {filterCount > 0 && (
              <span className="text-[10px] font-bold rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 min-w-[18px] text-center">
                {filterCount}
              </span>
            )}
          </button>
        )}

        {sortControl && <div className="hidden sm:block lg:hidden">{sortControl}</div>}
      </div>
    </div>
  );
}
